import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Loader2, Pencil, Save } from "lucide-react";
import { toast } from "sonner";

const VERTICAL_LABEL = {
  pet: "Pet Shop / Veterinária",
  barbearia: "Barbearia",
  beleza: "Salão de Beleza / Estética",
  estudio: "Estúdio (Foto & Maquiagem)",
};

export default function MasterTenantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/master/tenants/${id}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="p-10 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  if (!data) return <div className="p-10 text-muted-foreground">Não encontrado.</div>;

  const t = data.tenant;
  const flags = Object.entries(t.feature_flags || {});

  return (
    <div className="p-6 md:p-10 max-w-7xl space-y-6">
      <Link to="/master/tenants" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Empresa</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1" data-testid="master-tenant-legal-name">{t.legal_name}</h1>
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <span className="font-mono">{t.slug}</span> · <Badge variant="outline">{t.status}</Badge> · <span data-testid="master-tenant-vertical">{VERTICAL_LABEL[t.vertical] || t.vertical}</span>
            {t.business_type && (
              <>
                · <Badge variant="outline" className="font-normal" data-testid="master-tenant-business-type">
                  Tipo: {t.business_type}
                </Badge>
              </>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setEditOpen(true)}
          data-testid="master-tenant-edit-btn"
          className="gap-2"
        >
          <Pencil className="w-3.5 h-3.5" /> Editar dados
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6 rounded-xl border-border">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Branding</div>
          <div className="space-y-1 text-sm">
            <div><span className="text-muted-foreground">Display:</span> {t.branding?.display_name}</div>
            <div><span className="text-muted-foreground">Cor primária (HSL):</span> {t.branding?.primary_color}</div>
            <div><span className="text-muted-foreground">Headline login:</span> {t.branding?.login_headline}</div>
          </div>
          <div className="mt-3 h-2 rounded-full" style={{ background: `hsl(${t.branding?.primary_color})` }} />
        </Card>

        <Card className="p-6 rounded-xl border-border">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Configurações</div>
          <div className="space-y-1 text-sm">
            <div><span className="text-muted-foreground">Plano:</span> {t.settings?.plan}</div>
            <div><span className="text-muted-foreground">Receita mensal recorrente:</span> R$ {(t.settings?.mrr || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
            <div><span className="text-muted-foreground">Fuso:</span> {t.settings?.timezone}</div>
            <div><span className="text-muted-foreground">Moeda:</span> {t.settings?.currency}</div>
          </div>
        </Card>

        <Card className="p-6 rounded-xl border-border md:col-span-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Recursos ativos</div>
          <div className="flex flex-wrap gap-2">
            {flags.length === 0 ? <div className="text-sm text-muted-foreground">Nenhum recurso configurado.</div> :
              flags.map(([k, v]) => (
                <span key={k} className={`px-2.5 py-1 rounded-full text-xs border ${v ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-muted text-muted-foreground border-border"}`}>
                  {k}{v ? "" : " (off)"}
                </span>
              ))}
          </div>
        </Card>

        <Card className="p-6 rounded-xl border-border md:col-span-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Usuários</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-2">Email</th><th className="text-left py-2">Nome</th><th className="text-left py-2">Papel</th></tr></thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id} className="border-t border-border">
                  <td className="py-2">{u.email}</td>
                  <td className="py-2">{u.name}</td>
                  <td className="py-2">{u.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <EditTenantDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        tenant={t}
        onSaved={async () => { setEditOpen(false); await load(); }}
      />
    </div>
  );
}

function EditTenantDialog({ open, onClose, tenant, onSaved }) {
  const [legal, setLegal] = useState(tenant?.legal_name || "");
  const [displayName, setDisplayName] = useState(tenant?.branding?.display_name || "");
  const [businessType, setBusinessType] = useState(tenant?.business_type || "");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmNiche, setConfirmNiche] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLegal(tenant?.legal_name || "");
      setDisplayName(tenant?.branding?.display_name || "");
      setBusinessType(tenant?.business_type || "");
      setConfirmNiche(false);
      setPreview(null);
      api.get("/public/business-types")
        .then(({ data }) => setBusinessTypes(data.items || []))
        .catch(() => setBusinessTypes([]));
    }
  }, [open, tenant]);

  const changedLegal = legal.trim() !== (tenant?.legal_name || "").trim();
  const changedDisplay = displayName.trim() !== (tenant?.branding?.display_name || "").trim();
  const changedBusinessType = businessType !== (tenant?.business_type || "");

  // Carrega preview da troca de nicho quando o usuário muda a seleção.
  useEffect(() => {
    if (!open || !tenant?.id) return;
    if (!changedBusinessType || !businessType) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    api
      .get(`/master/tenants/${tenant.id}/change-niche/preview`, {
        params: { business_type: businessType },
      })
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [businessType, changedBusinessType, open, tenant?.id]);

  const nicheRequiresConfirm = changedBusinessType && preview?.diff?.vertical_changed;
  const canSave =
    (changedLegal || changedDisplay || changedBusinessType) &&
    legal.trim().length >= 2 &&
    (!nicheRequiresConfirm || confirmNiche);

  // Agrupa business_types por categoria para o <select>.
  const groupedByCategory = useMemo(() => {
    const groups = {};
    for (const bt of businessTypes) {
      const cat = bt.category || "Outros";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(bt);
    }
    return Object.entries(groups);
  }, [businessTypes]);

  const save = async () => {
    if (nicheRequiresConfirm && !confirmNiche) {
      toast.warning("Marque a confirmação para trocar o nicho.");
      return;
    }
    setBusy(true);
    try {
      // 1) Se nicho mudou, chama change-niche (atomicamente aplica novo pack).
      if (changedBusinessType) {
        const { data } = await api.post(`/master/tenants/${tenant.id}/change-niche`, {
          business_type: businessType || null,
        });
        if (data?.vertical_changed) {
          const act = (data.activated_modules || []).length;
          const deact = (data.deactivated_modules || []).length;
          toast.success(
            `Nicho atualizado! ${act} módulo(s) ativado(s), ${deact} desativado(s).`,
          );
        } else {
          toast.success("Nicho atualizado.");
        }
      }
      // 2) Nome legal e display name — chamada separada se mudaram.
      if (changedLegal || changedDisplay) {
        const payload = {};
        if (changedLegal) payload.legal_name = legal.trim();
        if (changedDisplay)
          payload.branding = { ...(tenant.branding || {}), display_name: displayName.trim() };
        await api.put(`/master/tenants/${tenant.id}`, payload);
      }
      if (!changedBusinessType) toast.success("Empresa atualizada!");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="master-tenant-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar dados da empresa</DialogTitle>
          <DialogDescription>
            Nome, nome de exibição e nicho. Trocar o nicho reaplica
            automaticamente módulos, terminologia e campos do novo segmento.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="edit-legal">Nome legal</Label>
            <Input
              id="edit-legal"
              data-testid="master-tenant-edit-legal-name"
              value={legal}
              onChange={(e) => setLegal(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-display">Nome de exibição</Label>
            <Input
              id="edit-display"
              data-testid="master-tenant-edit-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              placeholder="Como aparece pros clientes finais"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-business-type">Nicho da empresa</Label>
            <select
              id="edit-business-type"
              data-testid="master-tenant-edit-business-type"
              value={businessType}
              onChange={(e) => {
                setBusinessType(e.target.value);
                setConfirmNiche(false);
              }}
              className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
            >
              <option value="">— nenhum —</option>
              {groupedByCategory.map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map((bt) => (
                    <option key={bt.key} value={bt.key}>
                      {bt.emoji} {bt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground">
              A troca do nicho reaplica módulos, terminologia e campos do novo
              segmento. Clientes, agendamentos e históricos são preservados.
            </p>
          </div>

          {previewLoading && (
            <div className="p-3 rounded-md bg-muted/40 border border-border text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculando mudanças...
            </div>
          )}

          {!previewLoading && preview && nicheRequiresConfirm && (
            <div
              className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-100 text-xs space-y-2"
              data-testid="master-tenant-niche-preview"
            >
              <div className="font-semibold flex items-center gap-2">
                Trocar nicho {preview.current.business_type_label || preview.current.vertical}{" "}
                → {preview.next.business_type_label}
              </div>
              <div className="space-y-1.5">
                <div>
                  <strong>Base</strong>:{" "}
                  <code>{preview.current.vertical}</code> →{" "}
                  <code>{preview.next.vertical}</code>
                </div>
                {preview.diff.activated_modules.length > 0 && (
                  <div>
                    <strong>Módulos ativados</strong>:{" "}
                    {preview.diff.activated_modules.join(", ")}
                  </div>
                )}
                {preview.diff.deactivated_modules.length > 0 && (
                  <div>
                    <strong>Módulos desativados</strong>:{" "}
                    {preview.diff.deactivated_modules.join(", ")}
                  </div>
                )}
                <div>
                  <strong>Terminologia</strong>: Profissional →{" "}
                  <code>{preview.next.terminology?.resource_singular || "—"}</code>, Cliente →{" "}
                  <code>{preview.next.terminology?.client_singular || "—"}</code>
                </div>
                <div>
                  <strong>Campos de cliente</strong>:{" "}
                  {preview.current.custom_fields_count} atuais serão substituídos por{" "}
                  {preview.next.custom_fields_count} do novo nicho.
                </div>
              </div>
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmNiche}
                  onChange={(e) => setConfirmNiche(e.target.checked)}
                  data-testid="master-tenant-edit-confirm-niche"
                />
                <span>
                  Confirmo a troca para <strong>{preview.next.business_type_label}</strong>{" "}
                  (base <code>{preview.next.vertical}</code>).
                </span>
              </label>
            </div>
          )}

          {!previewLoading && preview && changedBusinessType && !preview.diff.vertical_changed && (
            <div className="p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-900 dark:text-emerald-100 text-xs">
              A nova classificação <strong>{preview.next.business_type_label}</strong>{" "}
              compartilha a mesma base do nicho atual — nenhum módulo será alterado.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            onClick={save}
            disabled={!canSave || busy}
            data-testid="master-tenant-edit-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
